import { Auth } from 'aws-amplify';
import React, { useEffect, useRef, useState } from 'react';
import { Location, useLocation, useNavigate } from 'react-router-dom';

import Form, { FormData } from '../../../components/Form/Form';
import { useAppContext } from '../../../contexts/AppContext';
import { useExtendedTranslation } from '../../../hooks/useExtendedTranslation';
import AgreementFooter from '../../../overwrites/views/auth/shared/AgreementFooter/AgreementFooter';
import { FormContainer } from '../auth.styles';
import SignInHeader from '../shared/SignInHeader';
import { SignupStateProps } from '../SignupPage';
import { ConfirmButton, StyledUnauthenticatedLayout } from './SignupConfirmPage.styles';

enum FormKey {
  Password = 'password',
  Organization = 'organization',
}

const SignupConfirmPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation() as Location & { state: SignupStateProps };
  const { firstName, lastName, phone, email, inviterOrganization } = state || {};
  const { t, tSpecific } = useExtendedTranslation();
  const { showError } = useAppContext();
  const [loading, setLoading] = React.useState(false);
  const formRef = useRef(null);
  const [formData] = useState<FormData>({
    [FormKey.Organization]: inviterOrganization,
  });

  useEffect(() => {
    const isSignUpAvailable = process.env.REACT_APP_SIGNUP_WITHOUT_INVITE_ENABLE === 'true';
    if (!state) {
      if (isSignUpAvailable) {
        navigate('/signup', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    }
  }, []);

  const sanitizePhoneForCognito = () => {
    return phone.replace(/[^\d+]/g, '');
  };

  const handleSubmit = (data: FormData) => {
    const sanitizedEmail = email.toLowerCase().trim();
    const password = data[FormKey.Password] as string;
    void (async () => {
      try {
        setLoading(true);
        await Auth.signUp({
          username: sanitizedEmail,
          password,
          attributes: {
            given_name: firstName,
            family_name: lastName,
            email: sanitizedEmail,
            phone_number: sanitizePhoneForCognito(),
            'custom:first_name': firstName,
            'custom:last_name': lastName,
            'custom:phone': phone,
            'custom:registration_org': data[FormKey.Organization],
          },
        });
        setLoading(false);
        navigate('/verify', { state: { email: sanitizedEmail, password } });
      } catch (e: any) {
        showError(e.message);
        setLoading(false);
      }
    })();
  };

  // We pass the query param to the signup page, so preloaded data from the invite is enforced.
  const onGoBack = () => navigate(`/signup?invite=${state?.inviteQueryParam}`, { replace: true, state });

  return (
    <StyledUnauthenticatedLayout
      title={tSpecific('auth.signUp.title')}
      header={<SignInHeader />}
      showBack
      {...{ onGoBack }}
    >
      <Form ref={formRef} data={formData} onSubmit={handleSubmit}>
        {({ input }) => (
          <FormContainer>
            {input({
              dataKey: FormKey.Organization,
              required: true,
              errorRequired: t('auth.error.organization'),
              disabled: !!inviterOrganization?.length,
              label: t('auth.signUp.organization.label'),
              placeholder: t('auth.signUp.organization.placeholder'),
            })}
            {input({
              dataKey: FormKey.Password,
              required: true,
              errorRequired: t('auth.error.password'),
              type: 'password',
              label: t('auth.common.password.label'),
              placeholder: t('auth.signUp.password.placeholder'),
              autoComplete: 'new-password',
            })}
            <ConfirmButton loading={loading} onPress={() => formRef.current.submit()}>
              {t('auth.signUp.confirm')}
            </ConfirmButton>
            <AgreementFooter />
          </FormContainer>
        )}
      </Form>
    </StyledUnauthenticatedLayout>
  );
};

export default SignupConfirmPage;
